import {Identifiable, Nameable, WithCoverAndLogoMobileOptimized} from './identifiable';
import {Merchant} from './merchant';
import {Weekday} from './weekday';
import {OrganizationSummary} from './organization';
import {UserLoyalty} from './user';
import {LocationsGroup} from './locations-group';
import {UsState} from './us-state';
import {UsCity} from './us-city';

export interface Campaign extends Identifiable, Nameable, WithCoverAndLogoMobileOptimized {
  title: string;
  type: CampaignType;
  isActive: boolean;
  status: CampaignStatus;
  userActionType: ActionType;
  merchant?: Merchant;
  locationCount?: number;
  locationsGroup?: LocationsGroup;
  reward: number;
  timeConditions?: TimeConditions;
  additionalConditions?: AdditionalConditions;
  inAppAction?: InAppAction;
  createdAt: Date;
  revenue: number;
  availableBudget: number;
  consumedBudget: number;
  online: boolean;
  // TODO delete/modify below property when BE is ready
  cashbackPercent?: number;
  organization: OrganizationSummary;
  userLoyalty: UserLoyalty;
  usState?: UsState;
  usCity?: UsCity;
}

export function getCampaignRemainingPercentileBudget(campaign: Campaign): number {
  return Math.trunc(campaign.availableBudget / (getCampaignTotalBudget(campaign)) * 100);
}

export function getCampaignConsumedPercentileBudget(campaign: Campaign): number {
  return Math.trunc(campaign.consumedBudget / (getCampaignTotalBudget(campaign)) * 100);
}

export function getCampaignRedeemedRewards(campaign: Campaign): number {
  return Math.trunc(campaign.consumedBudget / campaign.reward);
}

export function getCampaignRemainingRewards(campaign: Campaign): number {
  return Math.trunc(campaign.availableBudget / campaign.reward)
}

export function getCampaignTotalRewards(campaign: Campaign): number {
  return Math.trunc(getCampaignTotalBudget(campaign) / campaign.reward);
}

export function getCampaignTotalBudget(campaign: Campaign): number {
  return campaign.availableBudget + campaign.consumedBudget;
}

export interface AdditionalConditions {
  additionalConditionsType: AdditionalConditionsType;
  value: number;
}

export interface TimeConditions {
  daysOfWeek?: Weekday[];
  timeConditionsType: TimeConditionsType;
  startTime?: string;
  endTime?: string;
  startDate?: string;
  endDate?: string;
}

export interface UserRewardsSum {
  rewardsSum: number;
}

export interface UserRedeemedCampaignsStatistics {
  redeemedCount: number;
  rewardsSum: number;
  transactionsSum: number;
}

export enum CampaignStatus {
  PUBLISHED = 'PUBLISHED',
  DRAFT = 'DRAFT',
  ARCHIVED = 'ARCHIVED',
  STOPPED = 'STOPPED'
}

export enum ActionType {
  CARD_ACTION = 'CARD_ACTION',
  IN_APP_ACTION = 'IN_APP_ACTION'
}

export enum TimeConditionsType {
  NO_CONSTANT = 'NO_CONSTANT',
  PERIOD = 'PERIOD',
  RECURRING = 'RECURRING'
}

export enum AdditionalConditionsType {
  NUMBER_OF_TRANSACTIONS = 'NUMBER_OF_TRANSACTIONS',
  AMOUNT_SPENT = 'AMOUNT_SPENT',
  LOYALTY = 'LOYALTY',
}

export enum InAppAction {
  LINK_BANK_ACCOUNT = 'LINK_BANK_ACCOUNT',
  CONSOLIDATE_ACCOUNTS = 'CONSOLIDATE_ACCOUNTS',
  REFER_A_FRIEND = 'REFER_A_FRIEND'
}

export enum CampaignType {
  LOYALTY = 'LOYALTY',
  FREE_MONEY = 'FREE_MONEY'
}

export enum CampaignUserStatus {
  ACTIVE = 'ACTIVE',
  REDEEMED = 'REDEEMED'
}

interface CampaignI18nMessage {
  i18nMessage: string;
  params?: { [paramName: string]: any }
}

export function getCampaignLocationsI18nMessage(campaign: Campaign): CampaignI18nMessage {
  const locationsCount = campaign.locationCount != null ? campaign.locationCount : campaign.locationsGroup?.locations.length;

  if (locationsCount == null) {
    return {i18nMessage: 'actions.card.sentence.locations', params: {locations: 0}}
  }

  if (locationsCount === 1) {
    return {i18nMessage: 'actions.card.sentence.location'}
  }


  return {i18nMessage: 'actions.card.sentence.locations', params: {locations: locationsCount}};
}


export function getDaysLeft(endDate: string): number {
  const daysLeft = Math.ceil((new Date(endDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
  return Math.max(daysLeft, 0);
}
